import React, { useCallback, useState } from "react";
import "../Quiz/Quiz.css";
import "./PictureGame.css";
import { Seo } from "../../Shared/Seo/Seo.tsx";
import { IntroLead, IntroRest } from "../../Landing/IntroParagraphs.tsx";
import { findBySlug, quizRoutes } from "../../Shared/Seo/routeSeo.ts";
import { Operations } from "../../MathFactsGenerator/Constants.ts";
import { SettingsProvider } from "../../MathFactsGenerator/Settings/SettingsStore.tsx";
import { ValueRange } from "../../MathFactsGenerator/Settings/ValueRange.tsx";
import { canBuildDeck, flashCardSettings } from "../deck.ts";
import { createQuizQueue } from "../Quiz/quizQueue.ts";
import { pictureBestScoreKey, readBestScore, recordScore } from "../Quiz/bestScore.ts";
import { track } from "../../Shared/analytics.ts";
import { PictureGameRun, PictureResult } from "./PictureGameRun.tsx";
import { PictureGameSummary } from "./PictureGameSummary.tsx";
import { buildBoard, firstTryCount, pictureFor, Tile } from "./pictureGame.ts";
import { PictureId } from "./pictureArt.ts";
import { usePictureText } from "./pictureText.ts";
import { pictureGameEventParams } from "./gameEventParams.ts";
import { FactPicker, operationsOf } from "./FactPicker.tsx";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

interface PictureGamePageProps {
    slug: string;
    /** The route's `quiz`: the operation the picker opens on (ADR 0133 §4). */
    operation: Operations;
    /** The date the picture is chosen by. A test passes its own; the page reads the clock. */
    now?: () => Date;
}

interface Run {
    board: Tile[];
    picture: PictureId;
    /** Captured at Start, so a result is keyed by what was actually played. */
    operations: string[];
}

interface Summary {
    result: PictureResult;
    best: number | null;
    isNewBest: boolean;
    quizSlug: string | null;
}

/**
 * Hidden Picture (ADR 0133): a keyword landing page around a 5 x 5 board of math facts
 * over a seasonal picture. A quiz route carrying `game: "picture"`; its `quiz` is only
 * the operation the picker opens on. The facts come from the quiz's queue, drawn in the
 * Start handler and never while rendering (ADR 0001 §4); the picture is chosen from the
 * visitor's local date then and kept for Play again, so it cannot change mid-game.
 */
export function PictureGamePage({ slug, operation, now = () => new Date() }: PictureGamePageProps) {
    const entry = findBySlug(slug)!;
    const t = useTranslation();
    const pictureText = usePictureText();
    const [settings, setSettings] = useState(() => flashCardSettings(operation));
    const [run, setRun] = useState<Run | null>(null);
    const [runId, setRunId] = useState(0);
    const [summary, setSummary] = useState<Summary | null>(null);

    const ready = canBuildDeck(settings);
    const playing = run !== null;
    // The occasion the start screen names. Read on the client, in a render that only
    // ever happens there: the prerendered page holds no hint (ADR 0133 §2).
    const hintPicture = pictureFor(now());

    function start(picture: PictureId) {
        if (!ready) {
            return;
        }
        // Built here from settings as they stand, never during render: the generator
        // is non-deterministic.
        const board = buildBoard(createQuizQueue(settings).next);
        setRun({ board, picture, operations: operationsOf(settings) });
        setSummary(null);
        setRunId((id) => id + 1);
    }

    const handleFinish = useCallback(
        (result: PictureResult) => {
            const operations = run?.operations ?? [];
            const score = firstTryCount(result.tiles);
            const key = pictureBestScoreKey(operations);
            const previous = readBestScore(key);
            const best = recordScore(key, score);
            // After the store, as the quiz does: analytics never costs a best score.
            track("quiz_completed", pictureGameEventParams(operations, result.tiles));
            const plainQuiz = quizRoutes().find((route) => route.quiz === operations[0] && !route.game);
            setSummary({
                result,
                best: best > 0 ? best : null,
                isNewBest: previous !== null ? score > previous : score > 0,
                quizSlug: plainQuiz?.slug ?? null,
            });
            setRun(null);
        },
        [run]
    );

    return (
        <SettingsProvider settings={settings} setSettings={setSettings}>
            <Seo entry={entry} />
            <div className="landingContent">
                <h1>{entry.h1}</h1>
                <IntroLead paragraphs={entry.intro} />
            </div>

            <div className="quizTool pictureTool">
                {run && <PictureGameRun key={runId} board={run.board} picture={run.picture} onFinish={handleFinish} />}

                {!playing && summary && (
                    <PictureGameSummary
                        result={summary.result}
                        best={summary.best}
                        isNewBest={summary.isNewBest}
                        quizSlug={summary.quizSlug}
                        onPlayAgain={() => start(summary.result.picture)}
                    />
                )}

                {!playing && (
                    <div className="quizModePicker pictureIntro">
                        {!summary && (
                            <>
                                <p className="pictureIntroHow">{t("flashCards.pictureHowToPlay")}</p>
                                <p className="pictureHintLine">{pictureText.hint(hintPicture)}</p>
                            </>
                        )}
                        <p className="quizModePrompt">{t("flashCards.pictureWhichFacts")}</p>
                        <FactPicker settings={settings} setSettings={setSettings} label={t("flashCards.pictureWhichFacts")} />
                        {ready ? (
                            !summary && (
                                <button
                                    type="button"
                                    className="btn btn-primary pictureStart"
                                    onClick={() => start(pictureFor(now()))}
                                >
                                    {t("flashCards.pictureStart")}
                                </button>
                            )
                        ) : (
                            <p className="quizEmpty">{t("flashCards.gameEmptyNote")}</p>
                        )}
                    </div>
                )}

                {!playing && (
                    <details className="quizSettings">
                        <summary>{t("flashCards.changeWhatToPractise")}</summary>
                        <div className="valueRangeContainer">
                            <ValueRange RangeSettings={0} />
                            <ValueRange RangeSettings={1} />
                        </div>
                    </details>
                )}
            </div>

            <IntroRest paragraphs={entry.intro} />

            {entry.faq && entry.faq.length > 0 && (
                <section className="landingFaq">
                    <h2>{t("flashCards.frequentlyAskedQuestions")}</h2>
                    {entry.faq.map((item, i) => (
                        <div key={i} className="faqItem">
                            <h3>{item.q}</h3>
                            <p>{item.a}</p>
                        </div>
                    ))}
                </section>
            )}
        </SettingsProvider>
    );
}

export default PictureGamePage;
